/**
 * Speech Therapy Evaluation Note — options + empty payload.
 * Common header (1–6) via StNotesCommonHeader; clinical sections 7–13 per ST Eval mockups.
 */

import { buildDiagnosisAllergies } from "../../../patientForm.shared";
import { emptyStCommonHeader } from "./stNotesCommonHeaderUtils";
import { newGoalId } from "../ptNotes/ptGoals";

export const RESIDENCE_TYPE_OPTIONS = [
  "Private Home",
  "Apartment",
  "Assisted Living",
  "Skilled Nursing Facility",
  "SNF",
  "Other",
];

export const LIVES_WITH_OPTIONS = [
  "Alone",
  "Spouse",
  "Family",
  "Caregiver",
  "Other",
];

export const RAILING_PRESENT_OPTIONS = [
  "Yes - Right",
  "Yes - Left",
  "Yes - Both",
  "No",
];

export const HEARING_VISION_OPTIONS = [
  "Hearing WNL",
  "Vision Corrected",
  "Hearing Impaired",
];

export const ORAL_PHASE_OPTIONS = [
  "Reduced Mastication",
  "Anterior Bolus Loss",
  "Prolonged Transit",
];

export const PHARYNGEAL_PHASE_OPTIONS = [
  "Delayed Trigger",
  "Wet Vocal Quality",
  "Multiple Swallows",
];

export const SOLIDS_CONSISTENCY_OPTIONS = [
  "Regular",
  "Soft & Bite-Sized",
  "Minced & Moist",
  "Pureed",
  "NPO",
];

export const LIQUIDS_CONSISTENCY_OPTIONS = [
  "Thin",
  "Slightly Thick",
  "Moderately Thick",
  "Extremely Thick",
  "NPO",
];

export const COMPENSATORY_STRATEGY_OPTIONS = [
  "Chin Tuck",
  "Small Bolus Size",
  "Head Turn (R)",
  "Head Turn (L)",
  "Supraglottic Swallow",
  "Cyclic Ingestion",
];

export const ATTENTION_OPTIONS = [
  "WFL",
  "Mild Deficit",
  "Moderate Deficit",
  "Severe Deficit",
];

export const PRIMARY_INTERVENTION_OPTIONS = [
  "Rehabilitative",
  "Compensatory",
  "Maintenance",
  "Other",
];

export const GOAL_TYPE_OPTIONS = ["STG", "LTG"];

export const ST_EVAL_ARRAY_KEYS = [
  "hearingVisionStatus",
  "oralPhase",
  "pharyngealPhase",
  "compensatoryStrategies",
];

export const today = () => new Date().toISOString().slice(0, 10);

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

const nonEmpty = (v) => Boolean(String(v || "").trim());

export const formatReferringDiagnosis = (patient) => {
  const rows = buildDiagnosisAllergies(patient).filter(
    (d) => nonEmpty(d.primaryDiagnosis) || nonEmpty(d.diagnosisCode),
  );
  const primary = rows[0];
  if (!primary) return "";
  const code = String(primary.diagnosisCode || "").trim();
  const name = String(primary.primaryDiagnosis || "").trim();
  if (code && name && !name.startsWith(code)) return `${code} — ${name}`;
  return name || code;
};

export const emptyMeasurableGoalRow = (type = "STG", n = 1) => ({
  id: newGoalId(`mg-${n}`),
  type,
  description: "",
  target: "",
});

export const emptyMeasurableGoals = () => [
  emptyMeasurableGoalRow("STG", 1),
  emptyMeasurableGoalRow("STG", 2),
  emptyMeasurableGoalRow("LTG", 3),
];

export const normalizeMeasurableGoals = (rows, minCount = 3) => {
  const goals = Array.isArray(rows) ? rows : [];
  if (!goals.length) return emptyMeasurableGoals();
  const normalized = goals.map((row, idx) => ({
    id: row?.id || newGoalId(`mg-${idx + 1}`),
    type: row?.type === "LTG" ? "LTG" : "STG",
    description: String(row?.description || row?.goalText || "").trim(),
    target: String(row?.target || "").trim(),
  }));
  while (normalized.length < minCount) {
    normalized.push(
      emptyMeasurableGoalRow(
        normalized.length >= 2 ? "LTG" : "STG",
        normalized.length + 1,
      ),
    );
  }
  return normalized;
};

export const emptyStEvalPayload = () => ({
  billingCode: "G0153",
  timeIn: "",
  timeOut: "",
  commonHeader: emptyStCommonHeader(),

  referringDiagnosis: "",
  onsetSurgeryDate: "",

  // —— 7. Living Situation ——
  residenceType: "",
  residenceTypeOther: "",
  livesWith: "",
  livesWithOther: "",
  entranceAccess: "",
  railingPresent: "",

  // —— 8. Subjective History ——
  chiefComplaint: "",
  historyPresentIllness: "",
  priorLevelOfFunction: "",
  hearingVisionStatus: [],

  // —— 9. Communication Assessment ——
  receptiveLanguage: "",
  expressiveLanguage: "",
  speechProduction: "",
  pragmatics: "",

  // —— 10. Swallowing (Dysphagia) ——
  oralPhase: [],
  pharyngealPhase: [],
  solidsConsistency: "",
  liquidsConsistency: "",
  compensatoryStrategies: [],

  // —— 11. Cognitive Assessment ——
  orientation: "",
  attention: "",
  memory: "",
  problemSolvingReasoning: "",
  executiveFunction: "",

  // —— 12. Clinical Impression & Deficits ——
  clinicalImpression: "",

  // —— 13. Plan of Care ——
  visitFrequency: "",
  anticipatedDischargeDate: "",
  primaryIntervention: "",
  primaryInterventionOther: "",
  measurableGoals: emptyMeasurableGoals(),
});
